/**
 * React Router `location.state` shape used to hand an OAuth prefill handle
 * from the login / signup pages (after a `needs-signup` OAuth response) to
 * the signup page.
 *
 * The *handle* is an opaque string; the actual payload (including the
 * credential-equivalent `uid`) lives in an in-memory module store (see
 * `oauthPrefillStore.ts`). That split matters:
 *
 *   - `history.state` is persisted by the DOM's session history. On page
 *     reload it is restored. Putting the raw `uid` there would leave a
 *     password-equivalent value in browser-managed storage.
 *   - The in-memory store is per-JS-context. A full reload re-instantiates
 *     the module and the Map is empty, so even if `history.state` still
 *     carries the handle, the handle resolves to `null` and the signup
 *     page falls back to the landing screen. Intended behavior.
 *
 * The `uid` therefore only exists in RAM between the OAuth exchange and
 * the signup submit within the same tab session.
 *
 * `origin` records which page kicked off the OAuth attempt so the signup
 * page's Back button can return the user to the right screen — /login
 * when they came from the login page, /signup landing when they started
 * by clicking Google/Facebook directly on the signup form.
 */
export type OAuthPrefillOrigin = 'login' | 'signup';

export interface OAuthSignupLocationState {
  prefillHandle: string;
  origin: OAuthPrefillOrigin;
}

/**
 * Params that are safe to preserve from the current URL when navigating to
 * the signup form. These describe *context* (where the user came from, what
 * language, which restaurant, which app shell), never identity. Anything
 * else is dropped so leftover auto-trigger hints (`provider=google`) or
 * stale prefill flags from older revisions of this flow cannot leak into
 * the new navigation.
 *
 * `auth` is preserved because local dev uses `?auth=1` as the toggle to
 * render the AuthApp shell at `localhost:8080` (see `isAuthSubdomain` in
 * `@core/config/authDomain`). Dropping it would navigate the SPA into the
 * restaurant shell where /login and /signup do not exist.
 */
const PRESERVED_CONTEXT_PARAMS = [
  'return_url',
  'restaurant',
  'lang',
  'country',
  'dial_code',
  'auth',
] as const;

/**
 * Build the path+search string for the signup form, preserving only the
 * whitelisted context params from the caller's current URL. OAuth prefill
 * data does NOT go here — it is passed via React Router's `location.state`
 * by the caller.
 */
export function buildOAuthSignupPath(currentSearch: URLSearchParams): string {
  const params = new URLSearchParams();
  for (const key of PRESERVED_CONTEXT_PARAMS) {
    const value = currentSearch.get(key);
    if (value) params.set(key, value);
  }
  const qs = params.toString();
  return qs ? `/signup?${qs}` : '/signup';
}

/**
 * Build the path+search string for the login page after a user backs out of
 * a prefilled signup. Same context preservation, but we explicitly do NOT
 * carry `provider` forward: that param exists to auto-trigger OAuth on
 * `/login`, and re-triggering it after the user deliberately backed away
 * would create a loop.
 */
export function buildLoginPathFromOAuthBack(currentSearch: URLSearchParams): string {
  // The preserved list already excludes `provider`, so this is just an
  // explicit re-export with a semantic name for the caller. Keeping the two
  // functions separate documents intent at each call site.
  return buildOAuthSignupPath(currentSearch).replace('/signup', '/login');
}

/**
 * Defensive reader for `location.state`. Returns `{ handle, origin }` if
 * the state is well-formed, otherwise `null`. The signup page then looks
 * up the real payload in the in-memory store and uses `origin` to pick
 * the right destination when the user backs out.
 */
export function readOAuthPrefillHandle(
  state: unknown,
): { handle: string; origin: OAuthPrefillOrigin } | null {
  if (!state || typeof state !== 'object') return null;
  const { prefillHandle, origin } = state as {
    prefillHandle?: unknown;
    origin?: unknown;
  };
  if (typeof prefillHandle !== 'string' || prefillHandle.length === 0) return null;
  // Default to 'login' when origin is missing or unknown — matches the
  // pre-existing behavior from before the field was added and keeps the
  // safer back-destination (login page always exists).
  const safeOrigin: OAuthPrefillOrigin =
    origin === 'signup' || origin === 'login' ? origin : 'login';
  return { handle: prefillHandle, origin: safeOrigin };
}
